import type { CellState, TodayView } from '@aj/core';
import { Box, Button, Card, HStack, Text, VStack } from '@aj/ui';
import type { DayCell, DayColumn } from '@aj/ui';
import { useMemo, useState } from 'react';

import { useAppData } from '../../data/AppDataProvider.js';
import { useHabitGrid, useHomeStats, useReviewPlan } from '../../data/hooks.js';
import { useElementWidth } from '../../hooks/useElementWidth.js';
import { dateToMs } from '../../lib/dates.js';
import { BAND_GAP, BOTTOM_CLEARANCE, TOP_BAND } from '../../lib/layout.js';
import { useI18n, useT } from '../../providers/I18nProvider.js';
import { ComebackCard } from './ComebackCard.js';
import { ReviewCard } from './ReviewCard.js';
import { Compose } from './Compose.js';
import { Empty } from './Empty.js';
import { HabitList } from './HabitList.js';
import { Header } from './Header.js';
import { LogCard } from './LogCard.js';
import { StatBand } from './StatBand.js';
import { TasksCard } from './TasksCard.js';
import { fitDays } from './fitDays.js';
import { useHomeActions } from './useHomeActions.js';
import type { MessageKey } from '@aj/i18n';

/**
 * Home on a desktop: the same habit list, carrying its own past.
 *
 * Not a second view of the habits and not a "history" panel — the rows ARE the
 * rows, grown sideways into the wall chart students already screenshot off
 * Instagram and never actually build in Excel. Today's column is the ordinary
 * completion circle, so the daily action is one control on both layouts.
 *
 * How much past it shows is measured, not decided: two weeks on a small
 * laptop, up to six on a wide monitor, always in whole weeks (see `fitDays`).
 *
 * There is no week strip here. Seven liquid circles are a subset of the
 * columns directly below them; the phone shows them because it has no chart.
 */

/** `padding={5}` on the card, both sides — space the columns don't get. */
const CARD_PADDING = 40;

/** Every square says what it is out loud — one hue can't. */
const cellKey: Record<CellState, MessageKey> = {
  done: 'grid.done',
  floor: 'grid.floor',
  missed: 'grid.missed',
  off: 'grid.off',
  future: 'grid.future',
};

export type DashboardProps = {
  view: TodayView;
  onNavigate: (tab: string) => void;
};

export function Dashboard({ view, onNavigate }: DashboardProps) {
  const t = useT();
  const i18n = useI18n();
  const { ruleset, features } = useAppData();
  const home = useHomeActions();
  const review = useReviewPlan();
  const [composing, setComposing] = useState(false);

  // The chart shows as much past as fits, remeasured on resize. What gets
  // measured is the SPACE, not the card — the card then shrinks to hug its
  // columns, and measuring something that resizes with its own content would
  // chase its own tail.
  const [measure, width] = useElementWidth();
  const grid = useHabitGrid(fitDays(width - CARD_PADDING));
  const stats = useHomeStats();

  const empty = view.habits.length === 0;

  /** Dates run oldest → today, so the editable tail is exactly the backfill
   *  window. Out-of-window squares render inert rather than failing on tap. */
  const columns = useMemo<DayColumn[]>(() => {
    const window = features.capture.allowBackfill ? ruleset.backfillWindowDays : 0;
    const oldestEditable = grid.dates.length - 1 - window;

    return grid.dates.map((date, index) => ({
      key: date,
      weekday: i18n.formatDate(dateToMs(date), { weekday: 'narrow' }),
      day: i18n.formatDate(dateToMs(date), { day: 'numeric' }),
      isToday: date === view.date,
      editable: index >= oldestEditable,
    }));
  }, [grid.dates, view.date, i18n, ruleset.backfillWindowDays, features.capture.allowBackfill]);

  const keptRatios = useMemo(
    () => Object.fromEntries(grid.rows.map((row) => [row.id, row.keptRatio])),
    [grid.rows],
  );

  const dayLine = useMemo(
    () => ({
      label: t('grid.dayLine'),
      points: grid.days.map((day) => {
        const date = i18n.formatDate(dateToMs(day.date), { day: 'numeric', month: 'short' });
        return {
          ratio: day.ratio,
          label: day.isToday
            ? t('grid.dayToday', { date })
            : day.ratio === null
              ? t('grid.dayRest', { date })
              : t('grid.dayRatio', { date, value: Math.round(day.ratio * 100) }),
        };
      }),
    }),
    [grid.days, t, i18n],
  );

  const cells = useMemo<Record<string, DayCell[]>>(() => {
    const dates = grid.dates.map((date) =>
      i18n.formatDate(dateToMs(date), { day: 'numeric', month: 'short' }),
    );

    return Object.fromEntries(
      grid.rows.map((row) => [
        row.id,
        row.cells.map((state, index) => ({
          state,
          label: t(cellKey[state], { name: row.name, date: dates[index] ?? '' }),
        })),
      ]),
    );
  }, [grid.rows, grid.dates, t, i18n]);

  // Padding and gutters belong to the shell; `main` only scrolls. The header
  // band is exactly `TOP_BAND` tall because the nav column opposite it is too
  // — that shared height is what lines the nav's top up with the chart's.
  const showEmpty = empty && !composing;

  return (
    <Box as="main" className={`min-h-0 flex-1 overflow-y-auto ${BOTTOM_CLEARANCE}`}>
      {/* This wrapper is what gets measured: full width whatever it holds. */}
      <Box ref={measure} className="w-full">
        {/* `w-fit`: the column is as wide as the chart, so the card ends where
            the last day does — and the greeting's numbers land on that same
            right edge instead of drifting off toward the window. */}
        <VStack gap={BAND_GAP} className={showEmpty ? 'w-full' : 'w-fit max-w-full min-w-0'}>
          <HStack align="center" className={TOP_BAND}>
            <Header
              view={view}
              onOpenProfile={() => onNavigate('me')}
              onOpenProgress={() => onNavigate('jatra')}
              className="w-full"
            />
          </HStack>

          {/* Above the chart. A returning student should meet the invitation
              before they meet a wall of gaps. */}
          {view.comeback && <ComebackCard comeback={view.comeback} padding={5} />}

          {review && <ReviewCard plan={review} padding={5} />}

          {/* Above the chart, not below it: the chart is the evidence and
              these are its summary, so they read in that order. Desktop only
              — the phone is the daily loop and gets one number (§53.6). */}
          {!showEmpty && <StatBand stats={stats} className="w-full" />}

          {showEmpty ? (
            <Empty onAdd={() => setComposing(true)} onTemplate={home.addStarterPack} hasGoal={view.goal !== null} />
          ) : (
            <Card padding={5} className="w-fit max-w-full min-w-0">
              <VStack gap={4}>
                <HStack gap={3} align="baseline" justify="between">
                  <Text variant="overline" tone="subtle">
                    {t('home.habits')}
                  </Text>
                  {view.progress.total > 0 && (
                    <Text variant="caption" tone="muted" numeric>
                      {t('home.doneOfTotal', {
                        done: view.progress.done,
                        total: view.progress.total,
                      })}
                    </Text>
                  )}
                </HStack>

                {!empty && (
                  <HabitList
                    habits={view.habits}
                    schedules={home.schedules}
                    label={t('home.habits')}
                    days={{
                      columns,
                      cells,
                      onToggleDay: (habitId, index) => home.toggle(habitId, grid.dates[index]),
                    }}
                    keptRatios={keptRatios}
                    dayLine={dayLine}
                    onToggle={home.toggle}
                    onReorder={home.reorder}
                  />
                )}

                {composing ? (
                  <Compose
                    labelKey="home.habitName"
                    actionKey="home.addHabit"
                    onSubmit={home.addHabit}
                    onDismiss={() => setComposing(false)}
                  />
                ) : (
                  <Button
                    variant="secondary"
                    icon="plus"
                    onClick={() => setComposing(true)}
                    data-coach="add-habit"
                    className="self-start"
                  >
                    {t('home.addHabit')}
                  </Button>
                )}
              </VStack>
            </Card>
          )}

          {/* Below the chart, the two halves of the day side by side: promises
              on the left, evidence on the right. They wrap to one column when
              the chart is too narrow to seat both. */}
          {!showEmpty && (
            <HStack gap={BAND_GAP} align="start" className="w-full flex-wrap">
              {features.home.showTasks && (
                <TasksCard
                  tasks={view.tasks}
                  onAdd={home.addTask}
                  onToggle={home.toggleTask}
                  padding={5}
                  className="w-full max-w-md flex-1 basis-80"
                />
              )}
              {view.log && <LogCard log={view.log} padding={5} className="w-full max-w-md flex-1 basis-80" />}
            </HStack>
          )}
        </VStack>
      </Box>
    </Box>
  );
}
